import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Archive, Ban, BellOff, Bookmark, Crown, Flag, Frown, History, LifeBuoy, Pin, ShieldAlert, ShieldBan, Star, Target, Trash2, UserX, type LucideIcon } from "lucide-react";
import { Badge, type Tone } from "@/components/ui/badge";
import { Tooltip } from "@/components/ui/overlays";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { useT } from "@/lib/i18n";
import type { CommenterMark, ContactBlockState } from "@/lib/types";
import { cn } from "@/lib/utils";
import { sortFlags, useFlagLabel, useTags } from "./hooks";

/** Built-in flag look: icon + tone (design tokens only). */
export const FLAG_META: Record<string, { icon: LucideIcon; tone: Tone }> = {
  spam: { icon: ShieldBan, tone: "bad" },
  important: { icon: Star, tone: "warn" },
  follow_up: { icon: Bookmark, tone: "info" },
  lead: { icon: Target, tone: "ok" },
  complaint: { icon: Frown, tone: "warn" },
  vip: { icon: Crown, tone: "violet" },
};

/** One flag or custom tag chip. Custom tags show their colour as a dot. */
export function FlagChip({ flag, size = "xs" }: { flag: string; size?: "xs" | "sm" }) {
  const label = useFlagLabel();
  const { byKey } = useTags();
  const meta = FLAG_META[flag];
  if (meta) {
    const Icon = meta.icon;
    return (
      <Badge size={size} tone={meta.tone} icon={<Icon />} data-flag={flag}>
        {label(flag)}
      </Badge>
    );
  }
  const color = byKey.get(flag)?.color;
  return (
    <Badge size={size} tone="outline" icon={<span aria-hidden className="size-2 rounded-full" style={{ background: color ?? "var(--ink-3)" }} />} data-flag={flag}>
      {label(flag)}
    </Badge>
  );
}

/** Flags of an item (animated in/out), capped with a "+N". */
export function FlagChips({ flags, max = 3, size = "xs", className }: { flags: string[] | undefined; max?: number; size?: "xs" | "sm"; className?: string }) {
  const { byKey } = useTags();
  const reduce = useReducedMotion();
  const list = sortFlags(flags ?? [], byKey);
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span className={cn("inline-flex min-w-0 flex-wrap items-center gap-1", className)} data-testid="flag-chips">
      <AnimatePresence initial={false}>
        {shown.map((f) => (
          <motion.span key={f} initial={reduce ? false : { opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={reduce ? undefined : { opacity: 0, scale: 0.8 }} transition={{ duration: 0.15 }}>
            <FlagChip flag={f} size={size} />
          </motion.span>
        ))}
      </AnimatePresence>
      {rest > 0 && (
        <Badge size={size} tone="neutral" mono>
          +{rest}
        </Badge>
      )}
    </span>
  );
}

/** "Deleted on Instagram" with when, by whom and how Malfak learned it. */
/** `plain`: no tooltip/focus (inside a row button, where nested interactive content is invalid); the tip goes to `title`. */
export function DeletedBadge({ at, by, source, size = "xs", plain }: { at?: string | null; by?: string | null; source?: string | null; size?: "xs" | "sm"; plain?: boolean }) {
  const { t } = useT();
  const tip = t("moderation.badges.deletedTip", {
    when: at ? `${fmtRelative(at)} (${fmtDateTime(at)})` : "",
    by: t(`moderation.badges.deletedBy.${(by ?? "unknown") as "unknown"}`),
    source: t(`moderation.badges.source.${(source ?? "webhook") as "webhook"}`),
  });
  if (plain)
    return (
      <Badge size={size} tone="bad" icon={<Trash2 />} title={tip} data-testid="deleted-badge">
        {t("moderation.badges.deleted")}
      </Badge>
    );
  return (
    <Tooltip content={<span className="block max-w-[260px]">{tip}</span>}>
      <span tabIndex={0} className="inline-flex rounded-md outline-none focus-visible:shadow-[var(--ring)]" data-testid="deleted-badge" aria-label={`${t("moderation.badges.deleted")}. ${tip}`}>
        <Badge size={size} tone="bad" icon={<Trash2 />}>
          {t("moderation.badges.deleted")}
        </Badge>
      </span>
    </Tooltip>
  );
}

export function RecoveredBadge({ size = "xs", plain }: { size?: "xs" | "sm"; plain?: boolean }) {
  const { t } = useT();
  if (plain)
    return (
      <Badge size={size} tone="info" icon={<LifeBuoy />} title={t("moderation.badges.recoveredTip")} data-testid="recovered-badge">
        {t("moderation.badges.recovered")}
      </Badge>
    );
  return (
    <Tooltip content={<span className="block max-w-[240px]">{t("moderation.badges.recoveredTip")}</span>}>
      <span tabIndex={0} className="inline-flex rounded-md outline-none focus-visible:shadow-[var(--ring)]" data-testid="recovered-badge">
        <Badge size={size} tone="info" icon={<LifeBuoy />}>
          {t("moderation.badges.recovered")}
        </Badge>
      </span>
    </Tooltip>
  );
}

export function PinnedBadge({ size = "xs" }: { size?: "xs" | "sm" }) {
  const { t } = useT();
  return (
    <Badge size={size} tone="accent" icon={<Pin />} data-testid="pinned-badge">
      {t("moderation.badges.pinned")}
    </Badge>
  );
}

export function ArchivedBadge({ size = "xs" }: { size?: "xs" | "sm" }) {
  const { t } = useT();
  return (
    <Badge size={size} tone="neutral" icon={<Archive />} data-testid="archived-badge">
      {t("moderation.badges.archived")}
    </Badge>
  );
}

export const MARK_META: Record<CommenterMark, { icon: LucideIcon; tone: Tone }> = {
  harmful: { icon: ShieldAlert, tone: "bad" },
  troll: { icon: UserX, tone: "warn" },
  vip: { icon: Crown, tone: "violet" },
};

export function CommenterMarkBadge({ mark, size = "xs" }: { mark: CommenterMark | null | undefined; size?: "xs" | "sm" | "md" }) {
  const { t } = useT();
  if (!mark) return null;
  const { icon: Icon, tone } = MARK_META[mark];
  return (
    <Badge size={size} tone={tone} icon={<Icon />} data-testid="mark-badge" data-mark={mark}>
      {t(`moderation.marks.${mark}`)}
    </Badge>
  );
}

/**
 * "Blocked" (Block in Malfak) wherever a person's name appears. With the block details, the tooltip says where (all accounts
 * or one), who and when, and the reason. `plain`: no tooltip/focus (inside a row button; the tip goes to `title`).
 */
export function BlockedBadge({ block, blocked, size = "xs", plain }: { block?: ContactBlockState | null; blocked?: boolean; size?: "xs" | "sm" | "md"; plain?: boolean }) {
  const { t } = useT();
  if (!block && !blocked) return null;
  const tip = block
    ? [
        t("people.badge.blockedTip", {
          scope: block.accountId ? t("people.badge.scopeAccount") : t("people.badge.scopeAll"),
          by: block.byName ?? t("people.badge.someone"),
          when: `${fmtRelative(block.at)} (${fmtDateTime(block.at)})`,
        }),
        block.reason ? t("people.badge.blockedReason", { reason: block.reason }) : null,
      ].filter(Boolean).join(" · ")
    : t("people.badge.blocked");
  const badge = (
    <Badge size={size} tone="bad" icon={<Ban />} data-testid="blocked-badge" title={plain ? tip : undefined}>
      {t("people.badge.blocked")}
    </Badge>
  );
  if (plain || !block) return badge;
  return (
    <Tooltip content={<span className="block max-w-[280px]">{tip}</span>}>
      <span tabIndex={0} className="inline-flex rounded-md outline-none focus-visible:shadow-[var(--ring)]" aria-label={`${t("people.badge.blocked")}. ${tip}`}>
        {badge}
      </span>
    </Tooltip>
  );
}

/** A muted conversation (its person is blocked in Malfak): kept, out of the default inbox. */
export function MutedBadge({ size = "xs" }: { size?: "xs" | "sm" }) {
  const { t } = useT();
  return (
    <Badge size={size} tone="neutral" icon={<BellOff />} data-testid="muted-badge" title={t("people.badge.mutedTip")}>
      {t("people.badge.muted")}
    </Badge>
  );
}

/** A comment Malfak hid because its author is blocked. */
export function HiddenByBlockBadge({ size = "xs" }: { size?: "xs" | "sm" }) {
  const { t } = useT();
  return (
    <Badge size={size} tone="warn" icon={<Ban />} data-testid="hidden-by-block" title={t("people.badge.hiddenByBlockTip")}>
      {t("people.badge.hiddenByBlock")}
    </Badge>
  );
}

/** Small "edited"/"history" pill used on DMs. */
export function EditedPill({ count }: { count: number }) {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-ink-3">
      <History className="size-3" />
      {t("moderation.badges.edited")}
      {count > 1 ? ` ×${count}` : ""}
    </span>
  );
}

export { Flag as FlagIcon };
